import {
  type ActionCreatorWithPayload,
  type AnyListenerPredicate,
  createAsyncThunk,
  createSelector,
  type ThunkDispatch,
  type UnknownAction,
  unwrapResult,
} from '@reduxjs/toolkit';
import type { MarketSecurityStatus } from '@talos/kyoko';
import { streamingDataSlice } from '../../../providers/AppStateProvider/streamingDataSlice';
import type { AppExtraArgument, AppState, AppStateListenerStart } from '../../../providers/AppStateProvider/types';
import type { MarketSelectorItem, MarketSelectorValue } from '../../MarketSelector/types';
import type { OMSView } from '../OMSView';

interface GetMarketSelectorItemsListenerParams<BuildingParams> {
  /** The startListening param from setupListeners */
  startListening: AppStateListenerStart;
  /** Some unique id for the slice+action we're in */
  id: string;
  legIndex?: number;
  /** A predicate to decide when to start and restart the listener */
  predicate: AnyListenerPredicate<AppState>;
  /** A selector for grabbing the MarketSecurityStatus request params */
  selectMSSRequestParams: (state: AppState) => { Symbols: string[] } | undefined;
  /** A selector for grabbing all parameters needed to build the market selector items array */
  selectItemBuildingParams: (state: AppState) => BuildingParams;
  /** The market selector item building function */
  buildItems: (
    state: AppState,
    { mss }: { mss: Map<string, Map<string, MarketSecurityStatus>> | undefined },
    legIndex?: number
  ) => MarketSelectorItem[];
  /** On change handler. Called whenever the listener has a change in item to convey to the slice */
  onItemChange: (
    items: MarketSelectorItem[],
    dispatch: ThunkDispatch<AppState, AppExtraArgument, UnknownAction>
  ) => void;
  /** The OMSView this listener is relevant for. Whenever the view is something different than what you provide, the listener stops. */
  OMSView: OMSView;
}

/**
 * Returns the listener definition for listening to state, and rebuilds and emits new market selector items
 * whenever relevant changes occur
 */
export function getMarketSelectorItemsListener<BuildingParams>({
  id,
  legIndex,
  predicate,
  selectMSSRequestParams,
  startListening,
  selectItemBuildingParams,
  buildItems,
  onItemChange,
  OMSView,
}: GetMarketSelectorItemsListenerParams<BuildingParams>): Parameters<AppStateListenerStart>[0] {
  const selectMSSMap = createSelector(
    selectMSSRequestParams,
    (state: AppState) => state,
    (requestParams, state) => {
      if (!requestParams) {
        return undefined;
      }
      return streamingDataSlice.endpoints.getMarketSecurityStatuses.select(requestParams)(state).data;
    }
  );

  return {
    predicate: (action, currentState, originalState) => predicate(action, currentState, originalState),
    effect: async (_, listener) => {
      listener.cancelActiveListeners();

      if (OMSView !== listener.getState().OMS.openedView) {
        return;
      }

      let mssRequestParams = selectMSSRequestParams(listener.getState());
      let mssSubscription = mssRequestParams
        ? listener.dispatch(streamingDataSlice.endpoints.getMarketSecurityStatuses.initiate(mssRequestParams))
        : undefined;
      const unsubscribeMSSSubscription = () => {
        const subscription = mssSubscription;
        mssSubscription = undefined;
        subscription?.unsubscribe();
      };

      // If the MSS request ever changes, we need to somewhere instantiate a request for the new parameters. This listener takes care of that.
      const unsubscribeDynamicMSSInitiator = startListening({
        predicate: (_, next, prev) => selectMSSRequestParams(prev) !== selectMSSRequestParams(next),
        effect: (_, listener) => {
          unsubscribeMSSSubscription();

          mssRequestParams = selectMSSRequestParams(listener.getState());
          mssSubscription = mssRequestParams
            ? listener.dispatch(streamingDataSlice.endpoints.getMarketSecurityStatuses.initiate(mssRequestParams))
            : undefined;
        },
      });

      const unsubscribeMSSChangeListener = startListening({
        predicate: (_, next, prev) => {
          const prevMSSMap = selectMSSMap(prev);
          const nextMSSMap = selectMSSMap(next);
          return prevMSSMap !== nextMSSMap && nextMSSMap != null;
        },
        effect: (_, listener) => {
          const mssMap = selectMSSMap(listener.getState());
          if (mssMap) {
            const newItems = buildItems(listener.getState(), { mss: mssMap }, legIndex);
            onItemChange(newItems, listener.dispatch);
          }
        },
      });

      // When the building params change, we trigger a re-build of the items
      const unsubscribeItemDepsChangeListener = startListening({
        predicate: (_, next, prev) => selectItemBuildingParams(prev) !== selectItemBuildingParams(next),
        effect: (_, listener) => {
          // Grab the most current state of the MSS map and use that to build with
          const mssMap = selectMSSMap(listener.getState());
          if (mssMap) {
            const newItems = buildItems(listener.getState(), { mss: mssMap }, legIndex);
            onItemChange(newItems, listener.dispatch);
          }
        },
      });

      listener.signal.addEventListener('abort', () => {
        unsubscribeItemDepsChangeListener();
        unsubscribeDynamicMSSInitiator();
        unsubscribeMSSChangeListener();
        unsubscribeMSSSubscription();
      });

      // Keep the listener alive until the order form is closed.
      await listener.condition(() => {
        const state = listener.getState();
        const isFormClosed = state.OMS.openedView !== OMSView;
        return isFormClosed;
      });
    },
  };
}

interface GetInitMarketsThunkParams {
  /** Some unique id of the slice */
  sliceID: string;
  /** A selector returning whether or not the markets can be instantiated */
  selectCanInit: (state: AppState) => boolean;
  /** A selector to grab MSS Request params. If undefined, items are built without MSS validation */
  selectMSSRequestParams: (state: AppState) => { Symbols: string[] } | undefined;
  /** MarketSelectorItem[] builder */
  buildItems: (
    state: AppState,
    { mss }: { mss: Map<string, Map<string, MarketSecurityStatus>> | undefined }
  ) => MarketSelectorItem[];
  /** The action to call with the built items */
  initMarketsField: ActionCreatorWithPayload<{
    desiredValue: MarketSelectorValue | undefined;
    items: MarketSelectorItem[];
    defaultToEmptyMarkets?: boolean;
    allowDisabledSelections?: boolean;
  }>;
}
/** Creates and returns a thunk which takes care of some logistics around how to init a markets field */
export function getInitMarketsThunk({
  sliceID,
  selectCanInit,
  selectMSSRequestParams,
  buildItems,
  initMarketsField,
}: GetInitMarketsThunkParams) {
  return createAsyncThunk<
    void,
    {
      desiredValue: MarketSelectorValue | undefined;
      defaultToEmptyMarkets?: boolean;
      allowDisabledSelections?: boolean;
    },
    { state: AppState; extra: AppExtraArgument }
  >(`${sliceID}/initMarketsFieldThunk`, async (params, thunk) => {
    // Let the implementer decide certain cases where we should hold off on initializing. One example could be if init is called before
    // a sub account is set in the trading account segregation case.
    if (!selectCanInit(thunk.getState())) {
      return;
    }

    const items = unwrapResult(
      await thunk.dispatch(getMarketSelectorItemsThunk({ selectMSSRequestParams, buildItems }))
    );
    if (!items) {
      return;
    }

    thunk.dispatch(
      initMarketsField({
        ...params,
        items,
      })
    );
  });
}

export const getMarketSelectorItemsThunk = createAsyncThunk<
  MarketSelectorItem[] | undefined,
  Pick<GetInitMarketsThunkParams, 'selectMSSRequestParams' | 'buildItems'>,
  { state: AppState; extra: AppExtraArgument }
>('getMarketSelectorItemsThunk', async (params, thunk) => {
  const mssRequestParams = params.selectMSSRequestParams(thunk.getState());
  if (!mssRequestParams) {
    return params.buildItems(thunk.getState(), { mss: undefined });
  }

  const mssAction = await thunk.dispatch(getMarketSecurityStatusesThunk({ requestParams: mssRequestParams }));
  const mss = unwrapResult(mssAction);
  return params.buildItems(thunk.getState(), { mss });
});

export const getMarketSecurityStatusesThunk = createAsyncThunk<
  Map<string, Map<string, MarketSecurityStatus>> | undefined,
  { requestParams: { Symbols: string[] } },
  { state: AppState; extra: AppExtraArgument }
>('getMarketSelectorItemsThunk', async (params, thunk) => {
  const resultPromise = thunk.dispatch(
    streamingDataSlice.endpoints.getMarketSecurityStatuses.initiate(params.requestParams)
  );

  let mss: Map<string, Map<string, MarketSecurityStatus>> | undefined = undefined;
  const requestTimeoutMs = thunk.getState().referenceData.settings.marketSelectionAsyncRefDataTimeoutMs;

  try {
    // wait for 1500ms. Whichever resolves first, the timeout or the request, we proceed. Either with or without the mss records.
    mss = await Promise.race([
      resultPromise.unwrap(),
      new Promise<undefined>(resolve => setTimeout(() => resolve(undefined), requestTimeoutMs)),
    ]);
  } catch (e) {
    mss = undefined;
  } finally {
    resultPromise.unsubscribe();
  }

  return mss;
});
